import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EditPencilCard } from '../../assets/icons/edit-pencil.svg';
import { ReactComponent as Plus } from '../../assets/icons/plus.svg';
import { ReactComponent as ChargerIcon } from '../../assets/icons/sidebar/sidebar-device.svg';
import { ReactComponent as SlashIcon } from '../../assets/icons/slash.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as TickCircleIcon } from '../../assets/icons/tick-circle-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAllDevices,
  fetchDeviceDetails,
  fetchDeviceHubFilters,
  setLimit,
  setPage,
  updateDeviceHubFilters,
  disableDeviceHubFilterApplied,
  setSortData,
  refreshDevices,
} from '../../redux/slices/DeviceHubSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import EventDeviceSidePanel from '../../shared-components/table-side-panel/EvDeviceSidePanel';
import { useTableSidePanel } from '../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { changeDateTimeFormat, removeEmptyKeys } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import deviceHubApi from '../../utils/api/device-hub';

import ConnectorSidePanel from './components/ConnectorSidePanel';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this once backend security endpoints/fields ship.
// import { DeviceSecurity, isSecurityException } from './components/security/securityProfile';

import './scss/ev-device-hub.scss';
import './components/security/security.scss';

interface EventDeviceData {
  _id: string;
  brandId: {
    _id: string;
    name: string;
  };
  modelId: {
    _id: string;
    name: string;
  };
  serialNumber: string;
  brandName?: string;
  modelName?: string;
  currentType: 'AC' | 'DC';
  deviceOwnerType: string;
  connectorInformation: {
    connectorType: string;
    connectorFormat: string;
    maxVoltage: number;
    maxCurrent: number;
    connectorRating: number;
    unit: string;
    _id: string;
  }[];
  protocolSupport: string[];
  status: string;
  associatedCPO?: string;
  createdAt: string;
  deviceOwner: string;
  associatedAt?: string;
  tenantName?: string;
  endConsumerName?: string;
  attachedBy?: string;
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  // security?: DeviceSecurity;
}

// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
// PR #1130/#1143). Re-enable by uncommenting this block once backend security
// endpoints/fields ship.
/*
function getRowSecurity(row: any): DeviceSecurity {
  return {
    expectedSecurityProfile: row?.security?.expectedSecurityProfile ?? row?.expectedSecurityProfile,
    achievedSecurityProfile: row?.security?.achievedSecurityProfile ?? row?.achievedSecurityProfile,
    commissioned: row?.security?.commissioned ?? row?.commissioned,
  };
}

function renderExpectedProfile(row: any) {
  const security = getRowSecurity(row);
  return <p>{security.expectedSecurityProfile ?? '-'}</p>;
}

function renderAchievedProfile(t: any, row: any) {
  const security = getRowSecurity(row);
  if (security.achievedSecurityProfile === null || security.achievedSecurityProfile === undefined) {
    return <p>{t('notConnectedYet')}</p>;
  }
  return isSecurityException(security) ? (
    <span className="sp-badge error" title={t('belowExpectedProfile')}>
      {security.achievedSecurityProfile}
    </span>
  ) : (
    <p>{security.achievedSecurityProfile}</p>
  );
}

function renderCommissioned(t: any, row: any) {
  const { commissioned } = getRowSecurity(row);
  return (
    <span className={`sp-badge ${commissioned ? 'success' : ''}`}>
      {commissioned ? t('commissioned') : t('notCommissioned')}
    </span>
  );
}
*/

/**
 * Renders the Access Status badge
 * @param {any} t - Translation function
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderAccessStatus(t: any, row: any) {
  const isBlacklisted = row.status === 'blacklisted';
  const statusText = isBlacklisted ? t('blacklisted') : t('whitelisted');
  const badgeClass = isBlacklisted ? 'status-red' : 'status-green';
  return <span className={`device-status-badge ${badgeClass}`}>{statusText}</span>;
}

/**
 * Renders the Connection Status badge
 * @param {any} t - Translation function
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderConnectionStatus(t: any, row: any) {
  const rawStatus = row?.connectionStatus ?? (row?.isOnline ? 'online' : 'offline');
  const isOnline = String(rawStatus).toLowerCase() === 'online';
  const displayStatus = isOnline ? t('online') : t('offline');
  const badgeClass = isOnline ? 'status-green' : 'status-red';
  return <span className={`device-status-badge ${badgeClass}`}>{displayStatus}</span>;
}

/**
 * Renders the Attached Status badge
 * @param {any} t - Translation function
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderAttachedStatus(t: any, row: any) {
  const isAttached = row?.deviceOwner && row?.attachedBy;
  const statusText = isAttached ? t('attached') : t('notAttached');
  const badgeClass = isAttached ? 'status-green' : 'status-red';
  return (
    <div className="attached-status-cell">
      <span className={`device-status-badge ${badgeClass}`}>{statusText}</span>
      {isAttached && row.attachedAt && (
        <span className="date-text">{changeDateTimeFormat(row.attachedAt)}</span>
      )}
    </div>
  );
}

/**
 * Renders the Brand/Model Approval status badge
 * @param {any} row - Status string
 * @returns {any} JSX element
 */
// function renderApprovalStatus(status: string | undefined) {
//   const statusValue = status || 'Unapproved';
//   const statusLower = statusValue.toLowerCase();

//   /**
//    * Returns the appropriate icon based on status
//    * @returns JSX element
//    */
//   const getIcon = () => {
//     switch (statusLower) {
//       case 'approved':
//         return <TickCircleIcon width="14" height="14" />;
//       case 'rejected':
//         return <CloseCircleRedIcon width="14" height="14" />;
//       default:
//         return <SlashIcon width="14" height="14" />;
//     }
//   };

//   return (
//     <div className={`approval-status-badge ${statusLower}`}>
//       {getIcon()}
//       <span className="status-text">{statusValue}</span>
//     </div>
//   );
// }

/**
 * Renders brand name and model stack
 * @param row - row data
 * @returns JSX element
 */
const renderBrandModel = (row: EventDeviceData) => (
  <div className="brand-model-cell">
    <span className="brand-name">{row.brandId?.name || row?.brandName}</span>
    <span className="model-name">{row.modelId?.name || row?.modelName}</span>
  </div>
);

/**
 * Renders device type badge
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderType = (row: EventDeviceData) => (
  <span className={`type-badge ${row?.currentType?.toLowerCase()}`}>{row?.currentType}</span>
);

/**
 * Renders protocol support
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderProtocol = (row: EventDeviceData) => <span>{row.protocolSupport}</span>;

/**
 * Renders tenant name
 * @param {any} t - Translation function
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderTenant = (t: any, row: EventDeviceData) => {
  const ownerType = row.deviceOwnerType === 'commercial' ? t('cpo') : t('emsp');
  const attachedName = row.attachedBy;

  if (row.deviceOwnerType && attachedName) {
    return <span>{`${ownerType} (${attachedName})`}</span>;
  }
  return <span>-</span>;
};

/**
 * Renders the brand approval status badge
 * @param row - row data
 * @returns JSX element
 */
// const renderBrandStatus = (row: EventDeviceData) => renderApprovalStatus(row.brandStatus);

/**
 * Renders action buttons (edit + delete) for each row
 * @param row - Table row data
 * @returns JSX element
 */

interface EventDeviceHubProperties {
  /** When true, hides the '+ Add Device' button inside the table header */
  hideAddButton?: boolean;
  disablePageTitle?: boolean;
}

/**
 * EventDeviceHub Page Component — Device Hub List
 * @param {any} param0 - The param0
 * @param {any} param0.hideAddButton - The hideAddButton prop
 * @param {any} param0.disablePageTitle - The disablePageTitle prop
 * @returns {any} EventDeviceHub page
 */
function EventDeviceHub({
  hideAddButton = false,
  disablePageTitle = false,
}: EventDeviceHubProperties) {
  const { t } = useTranslation('deviceHub');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const {
    devices,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    deviceStats,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    filterListLoading,
  } = useAppSelector((state) => state.deviceHub);
  console.log('filters', filters);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatusDevice, setSelectedStatusDevice] = useState<EventDeviceData | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusModalLoading, setStatusModalLoading] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [exportLoader, setExportLoader] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Handle delete button click
   * @param {string} id - device ID
   */
  const onDeleteClick = (id: string) => {
    console.log('onDeleteClick called with id:', id);
    setSelectedDeviceId(id);
    setShowDeleteModal(true);
  };

  /**
   * Handle cancel delete
   */
  const handleCancelDelete = () => {
    console.log('handleCancelDelete called');
    setShowDeleteModal(false);
    setSelectedDeviceId(null);
  };

  /**
   * Opens the status change (blacklist / whitelist) modal
   * @param {EventDeviceData} device - Selected device data
   */
  const handleOpenStatusModal = (device: EventDeviceData) => {
    setSelectedStatusDevice(device);
    setStatusReason('');
    setShowStatusModal(true);
  };

  /**
   * Closes the status change modal
   */
  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedStatusDevice(null);
    setStatusReason('');
    setStatusModalLoading(false);
  };

  /**
   * Submits the blacklist or whitelist request
   * @returns {Promise<void>} A promise resolving when the request finishes
   */
  const handleSubmitStatusChange = async (): Promise<void> => {
    if (!selectedStatusDevice?.serialNumber || !statusReason.trim()) return;

    const isBlacklisted = selectedStatusDevice.status === 'blacklisted';
    const payload = {
      deviceSerialNumber: selectedStatusDevice.serialNumber,
      reason: statusReason.trim(),
    };

    setStatusModalLoading(true);
    try {
      if (isBlacklisted) {
        await deviceHubApi.whitelistDevice(payload);
        setNotificationMessage(t('whitelistSuccess'));
      } else {
        await deviceHubApi.blacklistDevice(payload);
        setNotificationMessage(t('blacklistSuccess'));
      }

      setShowStatusModal(false);
      setSelectedStatusDevice(null);
      setStatusReason('');
      setShowSuccessNotification(true);

      window.dispatchEvent(
        new CustomEvent('ev-device-updated', {
          detail: {
            deviceId: selectedStatusDevice?._id,
            serialNumber: selectedStatusDevice?.serialNumber,
          },
        }),
      );

      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllDevices({
          page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(fetchDeviceDetails());
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message:
            error?.response?.data?.message ||
            (isBlacklisted ? t('whitelistFailure') : t('blacklistFailure')),
          status: true,
        },
      });
    } finally {
      setStatusModalLoading(false);
    }
  };

  /**
   * Navigate to Add Device page
   */
  const handleAddDevice = () => {
    dispatch(
      setPageTitle({
        title: '',
        icon: null,
        count: null,
      }),
    );
    navigate('/ev-admin/ev-device-hub/add-device');
  };

  /**
   * Handle confirm delete
   * @param {any} [idFromProperties] - optional ID from DeleteWarning
   */
  const handleConfirmDelete = async (idFromProperties?: any) => {
    const idToDelete =
      (typeof idFromProperties === 'string' ? idFromProperties : null) || selectedDeviceId;
    console.log('handleConfirmDelete called, idToDelete:', idToDelete);
    if (!idToDelete) return;

    try {
      const response = await deviceHubApi.deleteDevice(idToDelete);
      console.log('Delete API response:', response);
      setNotificationMessage(t('deleteSuccess'));
      setShowSuccessNotification(true);
      setShowDeleteModal(false);
      setSelectedDeviceId(null);
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceHubId');
      parameters.delete('deviceId');
      setSearchParameters(parameters);
      // Refresh list and stats
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllDevices({
          page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(fetchDeviceDetails());
    } catch (error: any) {
      console.error('Delete API error:', error);
      setShowDeleteModal(false);
      setSelectedDeviceId(null);
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || t('deleteFailure'),
          status: true,
        },
      });
    }
  };

  /**
   * Opens the side panel on row click
   * @param {string} _id - device ID
   * @param {EventDeviceData} row - table row data
   */
  const handleOpenSidePanelOnRowClick = (_id: string, row: EventDeviceData) => {
    const targetId = row?._id || _id;
    if (!targetId) return;

    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('connectorDeviceId');
    parameters.delete('deviceId');
    parameters.set('deviceHubId', targetId);
    setSearchParameters(parameters);
  };

  /**
   * Navigates to the edit device page
   * @param {any} device - Device object
   */
  const handleEditDevice = (device: any) => {
    navigate(`../edit-device/${device._id || device.deviceId}`);
  };

  /**
   * Opens status change modal for device
   * @param {any} device - Device object
   */
  const handleStatusChange = (device: any) => {
    handleOpenStatusModal(device);
  };

  /**
   * Opens delete warning modal for device
   * @param {any} device - Device object
   */
  const handleDeleteDevice = (device: any) => {
    onDeleteClick(device._id || device.deviceId);
  };

  /**
   * Opens full device details page
   * @param {any} device - Device object
   * @param {boolean} [newTab] - Whether to open details in a new tab
   */
  const handleOpenDeviceDetails = (device: any, newTab?: boolean) => {
    const id = device?._id || device?.deviceId || device?.deviceHubId;
    if (!id) return;
    if (newTab) {
      window.open(`/ev-admin/ev-device-hub/device-details/${id}`, '_blank');
      return;
    }
    const searchParametersWithoutKey = new URLSearchParams(location.search);
    searchParametersWithoutKey.delete('deviceHubId');
    searchParametersWithoutKey.delete('action');
    const cleanSource =
      location.pathname +
      (searchParametersWithoutKey.toString() ? `?${searchParametersWithoutKey.toString()}` : '');
    navigate(`/ev-admin/ev-device-hub/device-details/${id}`, {
      state: { source: cleanSource },
    });
  };

  /**
   * Renders action buttons (expand + edit + block/unblock + delete) for each row
   * @param {EventDeviceData} row - Table row data
   * @returns {React.ReactElement} Action buttons element
   */
  const renderActions = (row: EventDeviceData) => {
    // Attached chargers can still be edited (protocols + security only, via the restricted
    // attached-settings form) — only deletion stays blocked while an owner is linked.
    const isLinked = row.deviceOwner && row.deviceOwner !== '-';
    const tooltipText = t('deleteOrEditRestrictionTooltip');

    const hasUpdatePermission = permissionCheck('devicesEvDeviceHubUpdate');
    const hasDeletePermission = permissionCheck('devicesEvDeviceHubDelete');

    const editTooltip = hasUpdatePermission ? t('edit') : t('noEditPermission');

    let deleteTooltip = t('delete');
    if (!hasDeletePermission) {
      deleteTooltip = t('noDeletePermission');
    } else if (isLinked) {
      deleteTooltip = tooltipText;
    }

    const isBlacklisted = row.status === 'blacklisted';
    let statusTooltip = isBlacklisted ? t('whitelistDevice') : t('blacklistDevice');
    if (!hasUpdatePermission) {
      statusTooltip = t('noStatusChangePermission');
    }

    return (
      <div className="action-buttons">
        <button
          type="button"
          className="action-btn expand-btn"
          onClick={(event) => {
            event.stopPropagation();
            handleOpenDeviceDetails(row, false);
          }}
          title={t('expandPages')}>
          <ExpandIcon />
        </button>
        <button
          type="button"
          className="action-btn edit-btn"
          disabled={!hasUpdatePermission}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`../edit-device/${row._id}`);
          }}
          title={editTooltip}>
          <EditPencilCard />
        </button>
        <button
          type="button"
          className={`action-btn ${isBlacklisted ? 'unblock-btn' : 'block-btn'}`}
          disabled={!hasUpdatePermission}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenStatusModal(row);
          }}
          title={statusTooltip}>
          {isBlacklisted ? <TickCircleIcon /> : <SlashIcon />}
        </button>
        <button
          type="button"
          className="action-btn delete-btn"
          disabled={!!isLinked || !hasDeletePermission}
          onClick={(event) => {
            event.stopPropagation();
            onDeleteClick(row._id);
          }}
          title={deleteTooltip}>
          <DeleteIcon />
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!disablePageTitle) {
      dispatch(
        setPageTitle({
          title: t('evDeviceHub'),
          icon: <ChargerIcon />,
          count: totalResults,
        }),
      );
    }

    dispatch(fetchDeviceDetails());
  }, [dispatch, totalResults, disablePageTitle, t]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchAllDevices({
        page: DefaultApiLimit.page,
        limit,
        start: '',
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );

    if (isFilterUpdated) {
      dispatch(disableDeviceHubFilterApplied());
    }
  }, [filters, isFilterUpdated, dispatch, limit, sortBy, sortField]);

  /**
   * Handles sort change
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handle page and limit change for pagination
   * @param {any} value - object containing new page and/or limit
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setPage(value.page));
    if (value.limit !== undefined) dispatch(setLimit(value.limit));
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Function to handle auto refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshDevices({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  const { renderSidePanelTopLeftContent, handleSidePanelClose } = useTableSidePanel({
    sessions: devices,
    onRefresh: handleRefresh,
    onEditDevice: handleEditDevice,
    onStatusChange: handleStatusChange,
    onDeleteDevice: handleDeleteDevice,
    onOpenDeviceDetails: handleOpenDeviceDetails,
  });

  // Deduplicate devices case-insensitively to avoid duplicate rows in the table
  const uniqueDevices = React.useMemo(() => {
    const map = new Map<string, any>();
    devices.forEach((d) => {
      const key = d.serialNumber?.toLowerCase();
      if (key && !map.has(key)) {
        map.set(key, d);
      }
    });
    return Array.from(map.values());
  }, [devices]);

  const columns: any[] = [
    {
      label: t('brandNameAndModel'),
      accessorKey: 'brandName',
      customData: renderBrandModel,
      sortAllow: true,
    },
    {
      label: t('deviceId'),
      accessorKey: 'serialNumber',
      headingDetails: true,
      headingDescription: (
        <span>
          {t('deviceIdDesc')}
        </span>
      ),
    },
    {
      label: t('currentType'),
      accessorKey: 'deviceType',
      customData: renderType,
      columnWidth: 100,
      headingDetails: true,
      headingDescription: <span>{t('currentTypeDesc')}</span>,
    },
    // {
    //   label: 'Brand Status',
    //   accessorKey: 'brandStatus',
    //   customData: renderBrandStatus,
    // },
    {
      label: t('connectors'),
      accessorKey: 'connectorInformation',
      sidePanelComponent: <ConnectorSidePanel fetchIdFrom="queryParams" />,
      queryParamsKey: 'connectorDeviceId',
      headingDetails: true,
      headingDescription: (
        <span>
          {t('connectorsDesc')}
        </span>
      ),
      /**
       * Renders the connectors cell
       * @param {any} row - Table row data
       * @returns {any} JSX element
       */
      customData: (row: EventDeviceData) => (
        <div
          className="connectors-cell"
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              const parameters = new URLSearchParams(searchParameters);
              parameters.delete('deviceId');
              parameters.delete('deviceHubId');
              parameters.set('connectorDeviceId', row.serialNumber);
              setSearchParameters(parameters);
            }
          }}
          onClick={(event) => {
            event.stopPropagation();
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('deviceId');
            parameters.delete('deviceHubId');
            parameters.set('connectorDeviceId', row.serialNumber);
            setSearchParameters(parameters);
          }}>
          <span className="connector-count hyperlink">
            {t('connectorCount', { count: row.connectorInformation?.length || 0 })}
          </span>
        </div>
      ),
    },
    {
      label: t('protocol'),
      accessorKey: 'protocolSupport',
      customData: renderProtocol,
      headingDetails: true,
      headingDescription: (
        <span>
          {t('protocolDesc')}
        </span>
      ),
    },
    {
      label: t('registrationStatus'),
      accessorKey: 'accessStatus',
      customData: renderAccessStatus.bind(null, t),
      headingDetails: true,
      headingDescription: (
        <span>{t('registrationStatusDesc')}</span>
      ),
      columnWidth: 150,
    },
    {
      label: t('connectionStatus'),
      accessorKey: 'connectionStatus',
      customData: renderConnectionStatus.bind(null, t),
      headingDetails: true,
      headingDescription: (
        <span>
          {t('connectionStatusDesc')}
        </span>
      ),
      columnWidth: 150,
    },
    {
      label: t('attachedStatus'),
      accessorKey: 'attachedAt',
      customData: renderAttachedStatus.bind(null, t),
      headingDetails: true,
      headingDescription: (
        <span>
          {t('attachedStatusDesc')}
        </span>
      ),
      // columnWidth: 150,
      sortAllow: true,
    },

    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting these columns once backend security
    // endpoints/fields ship.
    /*
    {
      label: t('expectedSecurityProfile'),
      accessorKey: 'expectedSecurityProfile',
      customData: renderExpectedProfile,
      columnWidth: 120,
      sortAllow: false,
    },
    {
      label: t('achievedSecurityProfile'),
      accessorKey: 'achievedSecurityProfile',
      customData: renderAchievedProfile.bind(null, t),
      columnWidth: 140,
      sortAllow: false,
    },
    {
      label: t('commissioning'),
      accessorKey: 'commissioned',
      customData: renderCommissioned.bind(null, t),
      columnWidth: 140,
      sortAllow: false,
    },
    */
    {
      label: t('attachedEntity'),
      accessorKey: 'deviceOwner',
      customData: renderTenant.bind(null, t),
      headingDetails: true,
      headingDescription: (
        <span>{t('attachedEntityDesc')}</span>
      ),
    },

    {
      label: t('action'),
      accessorKey: '_id',
      sticky: 'right' as const,
      customData: renderActions,
      headingDetails: true,
      headingDescription: (
        <span>{t('actionDesc')}</span>
      ),
    },
  ];

  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    { label: t('brandNameAndModel'), accessorKey: 'brandName' },
    { label: t('deviceId'), accessorKey: 'serialNumber' },
    { label: t('currentType'), accessorKey: 'deviceType' },
    { label: t('connectors'), accessorKey: 'connectorInformation' },
    { label: t('protocol'), accessorKey: 'protocolSupport' },
    { label: t('registrationStatus'), accessorKey: 'accessStatus' },
    { label: t('connectionStatus'), accessorKey: 'connectionStatus' },
    { label: t('attachedStatus'), accessorKey: 'attachedAt' },
    // SECURITY PROFILES DISABLED — backend not deployed yet, see column definitions above.
    // { label: t('expectedSecurityProfile'), accessorKey: 'expectedSecurityProfile' },
    // { label: t('achievedSecurityProfile'), accessorKey: 'achievedSecurityProfile' },
    // { label: t('commissioning'), accessorKey: 'commissioned' },
    { label: t('attachedEntity'), accessorKey: 'deviceOwner' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export devices data to PDF or CSV
   * @param {string} type - File type to export ('pdf' or 'csv')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    deviceHubApi
      .exportDevices(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawList = response?.data?.results || response?.data || [];
        const deviceList = Array.isArray(rawList) ? rawList : [];

        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = deviceList.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'brandName': {
                const brand = row?.brandId?.name || row?.brandName || '';
                const model = row?.modelId?.name || row?.modelName || '';
                if (brand && model) return `${brand} / ${model}`;
                return brand || model || '-';
              }

              case 'serialNumber':
                return row?.serialNumber || '-';

              case 'deviceType':
                return row?.currentType || row?.deviceType || '-';

              case 'connectorInformation': {
                const count = Array.isArray(row?.connectorInformation)
                  ? row.connectorInformation.length
                  : (Number(row?.connectorInformation) || Number(row?.connectorCount) || 0);
                return t('connectorCount', { count });
              }

              case 'protocolSupport':
                if (Array.isArray(row?.protocolSupport)) {
                  return row.protocolSupport.join(', ') || '-';
                }
                return row?.protocolSupport || '-';

              case 'accessStatus': {
                const isBlacklisted = row?.status === 'blacklisted';
                return isBlacklisted ? t('blacklisted') : t('whitelisted');
              }

              case 'connectionStatus': {
                const rawStatus = row?.connectionStatus ?? (row?.isOnline ? 'online' : 'offline');
                const isOnline = String(rawStatus).toLowerCase() === 'online';
                return isOnline ? t('online') : t('offline');
              }

              case 'attachedAt': {
                const isAttached = Boolean(row?.deviceOwner && row?.attachedBy);
                if (isAttached) {
                  return row?.attachedAt
                    ? `${t('attached')} (${changeDateTimeFormat(row.attachedAt)})`
                    : t('attached');
                }
                return t('notAttached');
              }

              // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
              // PR #1130/#1143). Re-enable by uncommenting these cases once backend security
              // endpoints/fields ship.
              /*
              case 'expectedSecurityProfile': {
                const { expectedSecurityProfile } = getRowSecurity(row);
                return expectedSecurityProfile === undefined ? '-' : String(expectedSecurityProfile);
              }

              case 'achievedSecurityProfile': {
                const { achievedSecurityProfile } = getRowSecurity(row);
                return achievedSecurityProfile === null || achievedSecurityProfile === undefined
                  ? t('notConnectedYet')
                  : String(achievedSecurityProfile);
              }

              case 'commissioned':
                return getRowSecurity(row).commissioned ? t('commissioned') : t('notCommissioned');
              */

              case 'deviceOwner': {
                const ownerType = row?.deviceOwnerType === 'commercial' ? t('cpo') : t('emsp');
                const attachedName = row?.attachedBy;
                if (row?.deviceOwnerType && attachedName) {
                  return `${ownerType} (${attachedName})`;
                }
                return '-';
              }

              default:
                return (row as any)?.[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'devices-list.csv');
          } else {
            exportPDF(headers, rows, 'devices-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch({
          type: 'notification/setNotificationMessage',
          payload: {
            type: 'error',
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
          },
        });
      });
  };

  return (
    <div className="ev-device-hub-wrap">
      <div className="ev-device-hub-container">
        {/* Stats summary bar */}
        {!hideAddButton && (
          <div className="stats-bar">
            <div className="stat-item">
              <span className="stat-label">{t('totalDevice')}</span>
              <span className="stat-value">{deviceStats.totalDevices || 0}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-label">{t('attached')}:</span>
              <span className="stat-value">{deviceStats.cpoAssociatedCount || 0}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-label">{t('notAttached')}:</span>
              <span className="stat-value">{deviceStats.nonAssociatedCount || 0}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-label">{t('blocked')}:</span>
              <span className="stat-value">{deviceStats.blockedDeviceCount || 0}</span>
            </div>
            <div className="stat-divider" />
            <span className="stat-badge ac-badge">
              <span className="badge-dot" />
              {t('ac')} {deviceStats.acDeviceCount || 0}
            </span>
            <span className="stat-badge dc-badge">
              <span className="badge-dot" />
              {t('dc')} {deviceStats.dcDeviceCount || 0}
            </span>
            <span className="stat-badge hybrid-badge">
              <span className="badge-dot" />
              {t('hybrid')} {deviceStats.hybridDeviceCount || 0}
            </span>
          </div>
        )}
        {/* Main Table */}
        <div>
          <CustomTable
            // dataKey="EventDeviceHub"
            version="v2"
            header={columns}
            data={uniqueDevices}
            serialNo
            loading={loading}
            totalResults={totalResults}
            limit={limit}
            page={page}
            height={339}
            totalPages={totalPages}
            onHiddenColumnsChange={setHiddenColumns}
            exportDataPdfOrCSV={exportDataPdfOrCSV}
            exportLoader={exportLoader}
            sizeChangeButtonRequired
            pageLimitChange={handlePageLimitChange}
            sortData={{ sortBy, sortField }}
            handleSortChange={handleSortFunction}
            filters={filters}
            filtersToDispatch={updateDeviceHubFilters}
            actionButton
            requiredRefreshToggle
            refreshFunction={handleAutoRefresh}
            refreshRequired
            handleRefresh={handleRefresh}
            hoverLabel={t('refresh')}
            rowClickEvent={handleOpenSidePanelOnRowClick}
            rowClickParamsKey="deviceHubId"
            rowClickSidePanel={<EventDeviceSidePanel fetchIdFrom="queryParams" />}
            rightTopContent={
              <div className="table-bottom-button-list">
                {!hideAddButton && permissionCheck('devicesEvDeviceHubAdd') && (
                  <div className="table-button-item">
                    <MyButton
                      label={t('addDevice')}
                      buttonType="submit"
                      onClickFunc={handleAddDevice}
                      svgIcon={<Plus />}
                    />
                  </div>
                )}
              </div>
            }
            onSidePanelClose={handleSidePanelClose}
            sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
            filterListLoading={filterListLoading}
            filterListToDispatch={fetchDeviceHubFilters}
            actionDropdown
            expandTableOpen
            expandTableOpenKey="EvDeviceHubList"
          />
        </div>
      </div>

      {/* Blacklist / Whitelist Confirmation Modal */}
      <ModalBox
        open={showStatusModal}
        closeModal={handleCloseStatusModal}
        title={null}
        header={false}
        content={
          <div className="device-status-modal-wrap">
            <div className="device-status-modal-content">
              <h1
                className={
                  selectedStatusDevice?.status === 'blacklisted'
                    ? 'whitelist-title'
                    : 'blacklist-title'
                }>
                {selectedStatusDevice?.status === 'blacklisted'
                  ? t('whitelistDevice')
                  : t('blacklistDevice')}
              </h1>
              <div className="device-status-info">
                <span className="info-label">{t('deviceSerialNumberLabel')}</span>
                <span className="info-value">
                  {selectedStatusDevice?.serialNumber || '--'}
                </span>
              </div>
              <div className="device-status-reason-field">
                <span className="reason-label">{t('reason')}</span>
                <textarea
                  id="device-status-reason"
                  className="reason-input"
                  aria-label="Reason"
                  placeholder={t('enterReason')}
                  value={statusReason}
                  onChange={(event) => setStatusReason(event.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <div className="device-status-modal-actions">
              <MyButton
                label={t('cancel')}
                buttonType="back"
                onClickFunc={handleCloseStatusModal}
                disableHoverEffect
              />
              <MyButton
                label={
                  selectedStatusDevice?.status === 'blacklisted'
                    ? t('whitelist')
                    : t('blacklist')
                }
                buttonType={
                  selectedStatusDevice?.status === 'blacklisted'
                    ? 'submit'
                    : 'error'
                }
                onClickFunc={handleSubmitStatusChange}
                loading={statusModalLoading}
                disableButton={!statusReason.trim()}
                hoverLabel={!statusReason.trim() ? 'Please fill the reason first' : ''}
              />
            </div>
          </div>
        }
      />

      {/* Delete Confirmation Modal */}
      <ModalBox
        open={showDeleteModal}
        closeModal={handleCancelDelete}
        title={null}
        header={false}
        content={
          <DeleteWarning
            selectedRowId={selectedDeviceId}
            title={t('deleteDeviceTitle')}
            description={t('deleteDeviceConfirm')}
            cancelButtonOnClick={handleCancelDelete}
            submitButtonOnClick={handleConfirmDelete}
          />
        }
      />

      {/* Success Notification */}
      {showSuccessNotification && (
        <ModalBox
          open={showSuccessNotification}
          closeModal={() => setShowSuccessNotification(false)}
          title={null}
          header={false}
          notification
          content={
            <SuccessNotification
              title={t('success')}
              description={notificationMessage}
              onNotificationClose={() => setShowSuccessNotification(false)}
            />
          }
        />
      )}
    </div>
  );
}

EventDeviceHub.defaultProps = {
  hideAddButton: false,
  disablePageTitle: false,
};

export default EventDeviceHub;
